import { isGlobalOptimalMove } from "@synapse/engine";
import type { GameState } from "../../stores/useGameStoreFacade";
import type { GameReport, MoveRecord } from "../../utils/gameReportUtils";

/**
 * What achievements judge: every move the player made, including moves a
 * backtrack later undid. Older reports have no moveHistory, so fall back to
 * the surviving path's choices, then to the bare player path.
 */
export function allMoves(report: GameReport): MoveRecord[] {
  if (Array.isArray(report.moveHistory)) return report.moveHistory;
  if (report.optimalChoices && report.optimalChoices.length > 0) {
    return report.optimalChoices;
  }
  const path = report.playerPath ?? [];
  const gold = report.optimalPath ?? [];
  return path.slice(1).map((to, i) => ({
    playerPosition: path[i],
    playerChose: to,
    isGlobalOptimal: isGlobalOptimalMove(gold, path[i], to),
    isLocalOptimal: false,
  }));
}

/** All moves except the winning click on the target, which every win ends with. */
export function freeMoves(
  report: GameReport,
  status: GameState["gameStatus"],
): MoveRecord[] {
  const moves = allMoves(report);
  return status === "won" ? moves.slice(0, -1) : moves;
}

/** Words the player stood on, in order: the start, then each word a move landed on. */
export function visitedWords(report: GameReport): string[] {
  if (!Array.isArray(report.moveHistory)) return report.playerPath ?? [];
  const start = report.startWord ?? report.playerPath?.[0];
  return [start, ...report.moveHistory.map((m) => m.playerChose)];
}

/** Gold-path words other than the start and target. */
export const goldInterior = (report: GameReport): Set<string> =>
  new Set((report.optimalPath ?? []).slice(1, -1));

/** Fewest moves before a per-move ratio achievement (Loose Cannon, Stealing Second) can count. */
export const MIN_MOVES_FOR_RATIO = 3;

/** Share of moves matching `pred`; 0 for no moves. */
export const shareOf = (
  moves: MoveRecord[],
  pred: (m: MoveRecord) => boolean,
): number =>
  moves.length === 0 ? 0 : moves.filter(pred).length / moves.length;
